import { useState, useCallback } from 'react';
import { User } from '../models/User';
import {
  getAllUsers,
  getUserById,
  addUser,
  updateUser,
  removeUser,
  authenticateUser,
  getUsersByRole,
  searchUsers,
  updateNotificationCount,
  setUserSuspended,
  setUserProfilePicture,
  removeUserProfilePicture,
  hasProfilePicture,
  getUsersWithProfilePictures,
  getUsersWithoutProfilePictures,
  updateUserCountry,
  setUserSuperAdmin,
  resetToDefaults,
  uploadAndProcessUserProfilePicture,
  uploadAndProcessMultipleUserProfilePictures,
  addUserWithProcessedProfilePicture,
  updateUserWithProcessedProfilePicture,
  getUserStatistics,
  addUserEnhanced,
  updateUserEnhanced,
  fixHavarSuperAdmin,
  resetUsersToDefaults,
  getUserByFirebaseUid,
  ProfilePictureProcessingOptions
} from '../data/userDatabase.ts';

export const useEnhancedUserDatabase = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Helper function to handle async operations with error handling
  const handleAsyncOperation = useCallback(async <T>(
    operation: () => Promise<T>,
    errorMessage: string = 'Operation failed'
  ): Promise<T | null> => {
    setIsProcessing(true);
    setError(null);
    
    try {
      const result = await operation();
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : errorMessage;
      setError(errorMsg);
      return null;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Basic user operations
  const users = {
    getAll: useCallback(() => getAllUsers(), []),
    getById: useCallback((id: string) => getUserById(id), []),
    // Note: getByUsername removed as username is no longer used
    getByRole: useCallback((isAdmin?: boolean, isSuperAdmin?: boolean) => 
      getUsersByRole(isAdmin, isSuperAdmin), []),
    search: useCallback((searchTerm: string) => searchUsers(searchTerm), []),
    getWithProfilePictures: useCallback(() => getUsersWithProfilePictures(), []),
    getWithoutProfilePictures: useCallback(() => getUsersWithoutProfilePictures(), []),
    getStatistics: useCallback(() => getUserStatistics(), [])
  };

  // User management operations
  const userManagement = {
    add: useCallback((user: User, profilePictureUrl?: string) => {
      try {
        if (profilePictureUrl) {
          addUserEnhanced(user, profilePictureUrl);
        } else {
          addUser(user);
        }
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add user');
        return false;
      }
    }, []),

    update: useCallback((user: User) => {
      try {
        updateUser(user);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update user');
        return false;
      }
    }, []),

    remove: useCallback((userId: string) => {
      try {
        removeUser(userId);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove user');
        return false;
      }
    }, []),

    authenticate: useCallback((username: string, password: string) => {
      try {
        return authenticateUser(username, password);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
        return null;
      }
    }, []),

    updateNotificationCount: useCallback((userId: string, count: number) => {
      try {
        updateNotificationCount(userId, count);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update notification count');
        return false;
      }
    }, []),

    setSuspended: useCallback((userId: string, suspended: boolean) => {
      try {
        setUserSuspended(userId, suspended);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update user suspension status');
        return false;
      }
    }, [])
  };

  // Enhanced profile picture operations with processing
  const profilePictures = {
    upload: useCallback(async (
      userId: string,
      imageSource: File | string,
      processingOptions?: ProfilePictureProcessingOptions
    ) => {
      return handleAsyncOperation(
        () => uploadAndProcessUserProfilePicture(userId, imageSource, processingOptions),
        'Failed to upload and process profile picture'
      );
    }, [handleAsyncOperation]),

    uploadMultiple: useCallback(async (
      userImages: Array<{
        userId: string;
        imageSource: File | string;
        processingOptions?: ProfilePictureProcessingOptions;
      }>
    ) => {
      return handleAsyncOperation(
        () => uploadAndProcessMultipleUserProfilePictures(userImages),
        'Failed to upload multiple profile pictures'
      );
    }, [handleAsyncOperation]),

    remove: useCallback((userId: string) => {
      try {
        removeUserProfilePicture(userId);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove profile picture');
        return false;
      }
    }, [])
  };

  // Enhanced user operations with profile picture processing
  const enhancedOperations = {
    addWithProcessedProfilePicture: useCallback(async (
      userData: Omit<User, 'id'>,
      imageSource: File | string,
      processingOptions?: ProfilePictureProcessingOptions,
      userId?: string
    ) => {
      return handleAsyncOperation(
        () => addUserWithProcessedProfilePicture(userData, imageSource, processingOptions, userId),
        'Failed to add user with processed profile picture'
      );
    }, [handleAsyncOperation]),

    updateWithProcessedProfilePicture: useCallback(async (
      user: User,
      imageSource?: File | string,
      processingOptions?: ProfilePictureProcessingOptions,
      removeExistingPicture?: boolean
    ) => {
      return handleAsyncOperation(
        async () => {
          await updateUserWithProcessedProfilePicture(
            user, 
            imageSource, 
            processingOptions, 
            removeExistingPicture
          );
          return true;
        },
        'Failed to update user with processed profile picture'
      );
    }, [handleAsyncOperation])
  };

  return {
    // State
    isProcessing,
    error,
    clearError,

    // Basic operations
    users,
    userManagement,
    
    // Enhanced operations with image processing
    profilePictures,
    enhancedOperations,

    // Helper utilities
    utils: {
      handleAsyncOperation
    }
  };
};

export default useEnhancedUserDatabase; 